<template>
  <Sheet v-model:open="drawer">
    <SheetContent class="settings-dialog" side="right">
      <SheetHeader class="settings-dialog__header">
        <SheetTitle>Настройки: {{ productName }}</SheetTitle>
        <SheetDescription>Поддержка и модули продукта</SheetDescription>
      </SheetHeader>
      <div class="settings-dialog__body">
        <div v-if="loading" class="ui-empty">
          <Spinner />
          <span>Загрузка настроек…</span>
        </div>
        <template v-else>
          <section class="settings-section">
            <h3 class="section-small-title">
              <ShieldCheck :size="17" />
              Поддержка
            </h3>
            <div class="settings-form-grid">
              <label class="ui-field">
                <span class="ui-label">Срок поддержки</span>
                <Input v-model="supportForm.support_till" type="date" />
              </label>
              <fieldset class="support-types-field">
                <legend class="ui-label">Тип поддержки</legend>
                <div class="support-type-options">
                  <label v-for="type in supportTypes" :key="type.id" class="check-label">
                    <input
                      v-model="supportForm.support_type_ids"
                      class="native-checkbox"
                      type="checkbox"
                      :value="type.id"
                    />
                    <span>{{ type.name }}</span>
                  </label>
                </div>
              </fieldset>
            </div>
            <div class="settings-checks">
              <label class="check-label">
                <Checkbox v-model="supportForm.support_suspended" />
                <span>Поддержка приостановлена</span>
              </label>
              <label class="check-label">
                <Checkbox v-model="supportForm.support_24on7" />
                <span>Поддержка 24/7</span>
              </label>
              <label class="check-label">
                <Checkbox v-model="supportForm.support_weekend" />
                <span>Поддержка в выходные</span>
              </label>
            </div>
            <label class="ui-field">
              <span class="ui-label">Заметки</span>
              <Textarea v-model="supportForm.support_notes" rows="2" />
            </label>
          </section>
          <section class="settings-section">
            <h3 class="section-small-title">
              <Puzzle :size="17" />
              Модули
            </h3>
            <div class="settings-module-filters">
              <label class="ui-field">
                <span class="ui-label">Поиск модуля</span>
                <Input v-model="moduleSearch" type="search" placeholder="Название модуля" />
              </label>
              <div class="segmented-control" role="group" aria-label="Тип модулей">
                <Button
                  v-for="filter in paymentFilters"
                  :key="filter.value"
                  size="sm"
                  :variant="payFilter === filter.value ? 'secondary' : 'ghost'"
                  :aria-pressed="payFilter === filter.value"
                  @click="payFilter = filter.value"
                >
                  {{ filter.label }}
                </Button>
              </div>
              <label class="check-label">
                <Checkbox v-model="connectedOnly" />
                <span>Только подключённые</span>
              </label>
            </div>
            <p v-if="!moduleSections.length" class="ui-empty">
              {{
                moduleSearch || payFilter !== 'all' || connectedOnly
                  ? 'Нет модулей по фильтру'
                  : 'Нет доступных модулей для этого продукта'
              }}
            </p>
            <details
              v-for="section in moduleSections"
              :key="section.key"
              class="settings-module-section"
              open
            >
              <summary>
                <span class="font-medium">{{ section.title }}</span>
                <Badge variant="outline">{{ section.modules.length }}</Badge>
              </summary>
              <div class="module-group-actions">
                <Button size="sm" variant="outline" @click="enableGroup(section.modules)">
                  Вкл. все
                </Button>
                <Button size="sm" variant="ghost" @click="disableGroup(section.modules)">
                  Выкл. все
                </Button>
              </div>
              <div class="module-list">
                <article
                  v-for="mod in section.modules"
                  :key="mod.id"
                  class="module-item"
                  :class="{
                    'module-item--active': getModuleState(mod.id).active,
                    'module-item--paid': mod.is_paid,
                    'module-item--free': !mod.is_paid,
                  }"
                >
                  <div class="module-item__header">
                    <label class="check-label module-check">
                      <Checkbox
                        :model-value="getModuleState(mod.id).active"
                        :aria-label="mod.name"
                        @update:model-value="(val) => updateModuleActive(mod.id, Boolean(val), mod)"
                      />
                      <span
                        class="mark"
                        :class="{ 'mark-paid': mod.is_paid }"
                        :title="mod.is_paid ? 'Платный' : undefined"
                      >
                        <template v-if="mod.is_paid">₽</template>
                      </span>
                      <span class="module-item__name">{{ mod.name }}</span>
                    </label>
                    <span class="module-price num">
                      {{
                        getModuleState(mod.id).active && getModuleState(mod.id).price
                          ? formatMoney(getModuleState(mod.id).price)
                          : ''
                      }}
                    </span>
                    <Button
                      v-if="getModuleState(mod.id).active"
                      variant="ghost"
                      size="icon"
                      class="fact-action"
                      :aria-label="
                        getModuleState(mod.id).notes ? 'Изменить примечание' : 'Добавить примечание'
                      "
                      :title="
                        getModuleState(mod.id).notes ? 'Изменить примечание' : 'Добавить примечание'
                      "
                      @click="isNotesOpen(mod.id) ? closeNotes(mod.id) : openNotes(mod.id)"
                    >
                      <NotebookPen :size="14" />
                    </Button>
                    <span v-else></span>
                  </div>
                  <div v-if="getModuleState(mod.id).active" class="module-item__details">
                    <div v-if="mod.is_paid" class="module-edit-grid">
                      <label class="ui-field">
                        <span class="ui-label">Стоимость</span>
                        <Input
                          :model-value="getModuleState(mod.id).price"
                          type="number"
                          step="0.01"
                          min="0"
                          @update:model-value="
                            (val: string | number) =>
                              updateModuleField(mod.id, 'price', String(val))
                          "
                        />
                      </label>
                      <label class="ui-field">
                        <span class="ui-label">Приобретён</span>
                        <Input
                          :model-value="getModuleState(mod.id).acquired_at"
                          type="date"
                          @update:model-value="
                            (val: string | number) =>
                              updateModuleField(mod.id, 'acquired_at', String(val))
                          "
                        />
                      </label>
                      <div class="ui-field">
                        <label :for="'module-license-' + mod.id" class="ui-label">
                          Срок действия
                        </label>
                        <Input
                          :id="'module-license-' + mod.id"
                          :model-value="getModuleState(mod.id).license_expires"
                          type="date"
                          :disabled="getModuleState(mod.id).isUnlimited"
                          @update:model-value="
                            (val: string | number) =>
                              updateModuleField(mod.id, 'license_expires', String(val))
                          "
                        />
                        <label class="check-label">
                          <Checkbox
                            :model-value="getModuleState(mod.id).isUnlimited"
                            @update:model-value="(val) => updateUnlimited(mod.id, Boolean(val))"
                          />
                          <span>Бессрочно</span>
                        </label>
                      </div>
                    </div>
                    <div v-if="isNotesOpen(mod.id)" class="mt-3">
                      <label class="ui-field">
                        <span class="ui-label">Примечание</span>
                        <Textarea
                          :model-value="getModuleState(mod.id).notes"
                          rows="2"
                          @update:model-value="
                            (val: string) => updateModuleField(mod.id, 'notes', val)
                          "
                        />
                      </label>
                      <Button size="sm" variant="ghost" class="mt-1" @click="closeNotes(mod.id)">
                        Свернуть
                      </Button>
                    </div>
                  </div>
                </article>
              </div>
            </details>
          </section>
        </template>
      </div>
      <SheetFooter class="settings-dialog__footer">
        <Button variant="outline" :disabled="saving" @click="drawer = false">Отмена</Button>
        <Button :disabled="loading || saving" @click="save">
          <Spinner v-if="saving" />
          Сохранить
        </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { ShieldCheck, Puzzle, NotebookPen } from '@lucide/vue';

import { ref, computed, watch } from 'vue';
import axios from 'axios';

type PayFilter = 'all' | 'paid' | 'free';

interface ModuleState {
  active: boolean;
  license_expires: string | null;
  isUnlimited: boolean;
  notes: string;
  price: string;
  acquired_at: string | null;
}

interface SupportForm {
  support_till: string | null;
  support_type_ids: number[];
  support_suspended: boolean;
  support_24on7: boolean;
  support_weekend: boolean;
  support_notes: string;
}

interface ModuleSectionVm {
  key: string;
  title: string;
  modules: CatalogModule[];
}

interface Props {
  modelValue: boolean;
  propertyId: number;
  productId: number;
  productName: string;
  currentModules: PropertyModule[];
  supportInfo?: ProductSupporter | null;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue', 'saved']);

const drawer = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const loading = ref(false);
const saving = ref(false);

const supportTypes = ref<SupportTypeDict[]>([]);
const supportForm = ref<SupportForm>({
  support_till: null,
  support_type_ids: [],
  support_suspended: false,
  support_24on7: false,
  support_weekend: false,
  support_notes: '',
});

const availableModules = ref<CatalogModule[]>([]);
const moduleSearch = ref('');
const payFilter = ref<PayFilter>('all');
const connectedOnly = ref(false);
const moduleStates = ref<Record<number, ModuleState>>({});
const openNotesIds = ref(new Set<number>());

function isNotesOpen(moduleId: number): boolean {
  return openNotesIds.value.has(moduleId);
}

function openNotes(moduleId: number) {
  openNotesIds.value = new Set([...openNotesIds.value, moduleId]);
}

function closeNotes(moduleId: number) {
  const next = new Set(openNotesIds.value);
  next.delete(moduleId);
  openNotesIds.value = next;
}

function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(n);
}

const assignableRows = computed(() => {
  const rows: { module: CatalogModule; group: { id: number; name: string } | null }[] = [];
  for (const root of availableModules.value) {
    const children = root.children?.length ? root.children : [];
    if (children.length) {
      for (const child of children) {
        rows.push({ module: child, group: { id: root.id, name: root.name } });
      }
    } else {
      rows.push({ module: root, group: null });
    }
  }
  return rows;
});

const filteredRows = computed(() => {
  return assignableRows.value.filter((row) => {
    const m = row.module;
    if (payFilter.value === 'paid' && !m.is_paid) return false;
    if (payFilter.value === 'free' && m.is_paid) return false;
    if (connectedOnly.value && !moduleStates.value[m.id]?.active) return false;
    if (moduleSearch.value.trim()) {
      const q = moduleSearch.value.toLowerCase().trim();
      const inName = m.name.toLowerCase().includes(q);
      const inGroup = row.group?.name.toLowerCase().includes(q);
      if (!inName && !inGroup) return false;
    }
    return true;
  });
});

const moduleSections = computed((): ModuleSectionVm[] => {
  const map = new Map<string, { title: string; modules: CatalogModule[] }>();
  for (const row of filteredRows.value) {
    const key = row.group ? `g-${row.group.id}` : 'standalone';
    const title = row.group ? row.group.name : 'Без группы';
    if (!map.has(key)) {
      map.set(key, { title, modules: [] });
    }
    map.get(key)?.modules.push(row.module);
  }
  const entries = [...map.entries()].map(([key, v]) => ({
    key,
    title: v.title,
    modules: [...v.modules].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  }));
  entries.sort((a, b) => {
    if (a.key === 'standalone') return 1;
    if (b.key === 'standalone') return -1;
    return a.title.localeCompare(b.title, 'ru');
  });
  return entries;
});

function getModuleState(moduleId: number): ModuleState {
  if (!moduleStates.value[moduleId]) {
    moduleStates.value[moduleId] = {
      active: false,
      license_expires: null,
      isUnlimited: true,
      notes: '',
      price: '',
      acquired_at: null,
    };
  }
  return moduleStates.value[moduleId];
}

function findCatalogModule(moduleId: number): CatalogModule | undefined {
  for (const row of assignableRows.value) {
    if (row.module.id === moduleId) return row.module;
  }
  return undefined;
}

function updateModuleActive(moduleId: number, active: boolean, catalogMod?: CatalogModule) {
  const state = getModuleState(moduleId);
  state.active = active;
  const mod = catalogMod ?? findCatalogModule(moduleId);
  if (active && mod?.is_paid && (!state.price || state.price === '') && mod.default_price) {
    state.price = String(mod.default_price);
  }
}

function updateModuleField(
  moduleId: number,
  field: 'license_expires' | 'notes' | 'price' | 'acquired_at',
  value: string | null
) {
  const state = getModuleState(moduleId);
  if (field === 'price') {
    state.price = value ?? '';
    return;
  }
  if (field === 'acquired_at') {
    state.acquired_at = value || null;
    return;
  }
  if (field === 'license_expires') {
    state.license_expires = value;
    return;
  }
  state.notes = value ?? '';
}

function updateUnlimited(moduleId: number, isUnlimited: boolean) {
  const state = getModuleState(moduleId);
  state.isUnlimited = isUnlimited;
  if (isUnlimited) {
    state.license_expires = null;
  }
}

function enableGroup(modules: CatalogModule[]) {
  for (const mod of modules) {
    updateModuleActive(mod.id, true, mod);
  }
}

function disableGroup(modules: CatalogModule[]) {
  for (const mod of modules) {
    getModuleState(mod.id).active = false;
  }
}

function initModuleStates() {
  moduleStates.value = {};
  openNotesIds.value = new Set();
  for (const row of assignableRows.value) {
    const module = row.module;
    const current = props.currentModules.find((m) => m.module_id === module.id);
    const defPrice = module.default_price != null ? String(module.default_price) : '';
    moduleStates.value[module.id] = {
      active: !!current,
      license_expires: current?.license_expires || null,
      isUnlimited: !current?.license_expires,
      notes: current?.notes || '',
      price:
        current?.price != null && String(current.price) !== ''
          ? String(current.price)
          : module.is_paid && defPrice
            ? defPrice
            : '',
      acquired_at: current?.acquired_at || null,
    };
  }
}

function initSupportForm() {
  const info = props.supportInfo;
  supportForm.value = {
    support_till: info?.support_till || null,
    support_type_ids: info?.support_type_ids ?? [],
    support_suspended: info?.support_suspended ?? false,
    support_24on7: info?.support_24on7 ?? false,
    support_weekend: info?.support_weekend ?? false,
    support_notes: info?.support_notes ?? '',
  };
}

async function loadData() {
  if (!props.productId) return;

  loading.value = true;
  try {
    const modulesResp = await axios.get(`/api/products/${props.productId}/`);
    const data = modulesResp.data as { modules?: CatalogModule[] };
    const roots = data.modules ?? [];
    availableModules.value = roots.map((r) => ({
      ...r,
      children: r.children ?? [],
    }));

    initModuleStates();
    initSupportForm();
  } catch (error) {
    console.error('Failed to load modules:', error);
  }

  try {
    const typesResp = await axios.get('/api/support-types/');
    supportTypes.value = typesResp.data.results ?? typesResp.data;
  } catch (error) {
    console.error('Failed to load support types:', error);
  }

  loading.value = false;
}

async function save() {
  saving.value = true;
  try {
    const invalidModules = Object.entries(moduleStates.value).filter(([moduleId, state]) => {
      if (!state.active) return false;
      const module = findCatalogModule(parseInt(moduleId, 10));
      return Boolean(module?.is_paid) && !state.isUnlimited && !state.license_expires;
    });

    if (invalidModules.length > 0) {
      alert('Укажите дату окончания лицензии для выбранных модулей или отметьте "Бессрочно"');
      saving.value = false;
      return;
    }

    const modulesToSave = Object.entries(moduleStates.value)
      .filter(([_, state]) => state.active)
      .map(([moduleId, state]) => {
        const parsedId = parseInt(moduleId, 10);
        const module = findCatalogModule(parsedId);
        const isPaid = Boolean(module?.is_paid);

        return {
          module_id: parsedId,
          license_expires: isPaid ? (state.isUnlimited ? null : state.license_expires) : null,
          notes: state.notes,
          price: isPaid ? (state.price === '' ? null : state.price) : null,
          acquired_at: isPaid ? state.acquired_at || null : null,
        };
      });

    await Promise.all([
      axios.post(`/api/properties/${props.propertyId}/update-support-info/`, {
        product_id: props.productId,
        ...supportForm.value,
      }),
      axios.post(`/api/properties/${props.propertyId}/update-product-modules/`, {
        product_id: props.productId,
        modules: modulesToSave,
      }),
    ]);

    emit('saved');
    drawer.value = false;
  } catch (error) {
    console.error('Failed to save:', error);
  } finally {
    saving.value = false;
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      loadData();
    }
  }
);

const paymentFilters: { value: PayFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'paid', label: 'Платные' },
  { value: 'free', label: 'Бесплатные' },
];
</script>
